import { channelListQuery, TvChannels } from '@funtech-int/kp-graphql-sdk/yango-movies-www';
import { createInfiniteQuery } from '@funtech-int/rival';
import { useInfinitePagination, useResources } from '@funtech-int/rival-react';

import { GraphqlClient } from '@/shared/lib/graphqlClient';
import { useGraphqlClient } from '@/shared/hooks/useGraphqlClient';
import { useProfileManager } from '@/shared/hooks/useProfileManager';

const CHANNEL_LIST_UPDATE_DELAY = 60 * 60 * 1000;

const CHANNELS_LIMIT = 40;

interface Params {
    categoryIds: number[];
}

interface ChannelListParams {
    categoryIds: number[];
    billingFeaturesCount: number;
}

interface ChannelListQueryContext {
    client: GraphqlClient;
    params: ChannelListParams;
}

interface ChannelListPageInfo {
    offset: number;
}

const channelListResourceQuery = createInfiniteQuery<
    ChannelListQueryContext,
    TvChannels | null,
    ChannelListPageInfo
>({
    key(ctx) {
        return [
            channelListQuery.name,
            ...ctx.params.categoryIds,
            ctx.params.billingFeaturesCount,
        ].join(':');
    },
    fetch(ctx, prev, pageInfo) {
        const offset = pageInfo?.offset ?? 0;

        return ctx.client
            .fetch({
                operation: channelListQuery,
                variables: {
                    categoryIds: ctx.params.categoryIds,
                    limit: CHANNELS_LIMIT,
                    offset,
                    withPrograms: true,
                },
            })
            .then(({ data }) => {
                if (!data || !data.channels.length) {
                    return prev ?? null;
                }

                const prevChannels = prev?.channels ?? [];

                return {
                    ...data,
                    channels: prevChannels.concat(data.channels),
                };
            });
    },
    nextPageInfo(data) {
        if (!data) {
            return;
        }

        const { limit, offset, total } = data;

        const loadedCount = limit + offset;
        if (loadedCount >= total) {
            return;
        }

        return {
            offset: offset + CHANNELS_LIMIT,
        };
    },
    toJSON(data: any) {
        return data;
    },
    hydrate(data: any) {
        return channelListQuery.hydrate(data) as any;
    },
});

export const useChannelListQuery = ({ categoryIds }: Params) => {
    const client = useGraphqlClient();

    const pagination = useInfinitePagination();
    const billingFeaturesCount = useProfileManager().billingFeatureNames.get().length;

    const [channelList] = useResources(
        channelListResourceQuery(
            {
                params: {
                    categoryIds,
                    billingFeaturesCount,
                },
                client,
            },
            { pagination, lifetime: CHANNEL_LIST_UPDATE_DELAY }
        )
    );

    return { channelList, pagination };
};
